import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import AppContext from "../../AppContext";
import { langComponent } from "../../constants";
import Overlay from "../Overlay";
import Panorama from "../common/Panorama";
import { fromNow } from "../../helpers/common.helper";

import "../../styles/projectView.scss";
import useWorks from "../../hooks/useWorks";
import useUsers from "../../hooks/useUsers";

import { ReactComponent as CloseSvg } from "../../assets/projectView/close.svg";
import { ReactComponent as LikeSvg } from "../../assets/icons/like.svg";
import { ReactComponent as LikeFilledSvg } from "../../assets/icons/liked-red.svg";
import { ReactComponent as ShareWhiteSvg } from "../../assets/icons/share.svg";
import { ReactComponent as PinSvg } from "../../assets/panorama/pin.svg";
import { ReactComponent as MessageSvg } from "../../assets/profile/message.svg";

import Loading from "./Loading";
import { toast } from "react-toastify";
import MoreBy from "./MoreBy";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper/modules";

import nextImg from "../../assets/home/next.png";
import prevImg from "../../assets/home/previous.png";
import Review from "./Review";

import programSketchUpImage from "../../assets/icons/programs/sketchUp.png";
import program3dsMaxImage from "../../assets/icons/programs/3dsMax.png";
import programRevitImage from "../../assets/icons/programs/autodeskRevit.png";
import programCinema4DImage from "../../assets/icons/programs/cinema4d.png";
import programUnrealImage from "../../assets/icons/programs/unreal.png";
import programRhinoImage from "../../assets/icons/programs/rhino.png";
import programAutocadImage from "../../assets/icons/programs/autocad.png";
import programArchicadImage from "../../assets/icons/programs/archicad.png";

import programVRayImage from "../../assets/icons/programs/vray.png";
import programCoronaImage from "../../assets/icons/programs/corona.png";
import programD5RenderImage from "../../assets/icons/programs/d5render.png";
import programEnscapeImage from "../../assets/icons/programs/enscape.jpg";
import programLumionImage from "../../assets/icons/programs/lumion.png";
import { useNavigate, useParams } from "react-router-dom";
import Share from "./Share";
import RatingStar from "../common/Rating";
import ChatSessionContext from "../../contexts/ChatSessionContext";

const assetsUri = import.meta.env.ASSETS_URI;

const PROGRAMS = {
  1: programSketchUpImage,
  2: program3dsMaxImage,
  3: programRevitImage,
  4: programCinema4DImage,
  5: programUnrealImage,
  6: programRhinoImage,
  7: programAutocadImage,
  8: programArchicadImage,
};

const RENDERERS = {
  1: programVRayImage,
  2: programCoronaImage,
  3: programLumionImage,
  4: programEnscapeImage,
  5: programD5RenderImage,
};

var musicPlayer = null;

const ProjectView = ({ onClose }) => {
  const {
    isAuth,
    user,
    designStyles,
    getLang,
    addLikes,
    removeLikes,
    projectViewing,
    plans,
    viewProfile,
    viewCompany,
  } = useContext(AppContext);
  const { startChat } = useContext(ChatSessionContext);
  const { id } = useParams();
  const TranslationCommon = getLang(langComponent.common);
  const TranslationWorkType = getLang(langComponent.workType);
  const TranslationSpaceType = getLang(langComponent.spaceType);
  const TranslationStyleType = getLang(langComponent.styleType);
  const [work, setWork] = useState(undefined);
  const { queryUsers, queryUserRatings, queryCompanies } = useUsers();
  const { getWork, queryWorks, addWorkView, getWorkRating } = useWorks();
  const [selectedImage, setSelectedImage] = useState(0);
  const [isPanorama, setIsPanorama] = useState(undefined);
  const [panoramas, setPanoramas] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [floorPlans, setFloorPlans] = useState([]);
  const [currentPanoramaIndex, setCurrentPanoramaIndex] = useState(0);
  const [isMusicPlaying, setIsMusicPlaying] = useState(undefined);
  const isMusicPlayingRef = useRef(null);
  const [moreWorksByAuthor, setMoreWorksByAuthor] = useState(undefined);
  const [recommendations, setRecommendations] = useState(undefined);
  const [isLiked, setIsLiked] = useState(undefined);
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [_, setInit] = useState();
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [reviews, setReviews] = useState(undefined);
  const [isToggleShare, setIsToggleShare] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const navigate = useNavigate();
  const widgetRef = useRef(null);

  useEffect(() => {
    return () => {
      // * Stop music player if exists
      if (musicPlayer) {
        musicPlayer.pause();
        musicPlayer = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!id) {
      navigate("/");
      return;
    }

    if (!designStyles) return;

    setWork(undefined);

    setIsLiked(user?.likes?.includes(id));
    addWorkView(id, user?._id);

    init();
  }, [id, designStyles]);

  const ref = React.useCallback((node) => {
    if (!node) return;

    widgetRef.current = window.SC.Widget(node);

    widgetRef.current.bind(window.SC.Widget.Events.READY, () => {
      console.log("SoundCloud widget ready!");
      widgetRef.current.setVolume(40);
      setIsMusicPlaying(false);
      isMusicPlayingRef.current = false;
    });

    widgetRef.current.bind(window.SC.Widget.Events.PLAY, () => {
      setIsMusicPlaying(true);
      isMusicPlayingRef.current = true;
    });

    widgetRef.current.bind(window.SC.Widget.Events.PAUSE, () => {
      setIsMusicPlaying(false);
      isMusicPlayingRef.current = false;
    });

    widgetRef.current.bind(window.SC.Widget.Events.FINISH, () => {
      console.log("SoundCloud widget is finished");

      widgetRef.current.seekTo(0);
      widgetRef.current.play();
    });
  }, []);

  const init = async () => {
    try {
      if (!id) return;

      const _work = await getWork(id);

      if (!_work || _work.deleted) {
        navigate("/404");
        return;
      }

      if (!_work.active && _work.createdBy !== user._id) {
        navigate("/404");
        return;
      }

      const _rating = await getWorkRating(id);

      // * Get owner if current user is not the owner
      if (_work.company) {
        const {
          data: [_company],
        } = await queryCompanies({ _id: _work.company }, 1, 1, {
          logo: 1,
          name: 1,
          enterprise: 1,
        });

        _work.owner = {
          _id: _company._id,
          isOwner: _work.createdBy === user?._id,
          avatar: _company.logo,
          name: _company.name,
          plan: _company.enterprise
            ? plans.find((p) => p.name === "Enterprise")?._id
            : plans.find((p) => p.name === "Business")?._id,
        };

        _work.company = _company;
      }

      if (!_work.company) {
        if (_work.createdBy === user?._id) {
          _work.owner = {
            _id: _work.createdBy,
            isOwner: true,
            avatar: user.avatar,
            name: `${user.firstname} ${user.surname || ""}`,
            plan: user.plan,
          };
        }

        if (_work.createdBy !== user?._id) {
          const {
            data: [_user],
          } = await queryUsers({ _id: _work.createdBy }, 1, 1, {
            avatar: 1,
            firstname: 1,
            surname: 1,
            plan: 1,
          });

          _work.owner = {
            _id: _work.createdBy,
            isOwner: false,
            avatar: _user.avatar,
            name: `${_user.firstname} ${_user.surname || ""}`,
            plan: _user.plan,
          };
        }
      }

      const isOwner = _work.company
        ? _work.company === user?.company
        : _work.createdBy === user?._id;

      setIsCreator(isOwner);

      // * Get work ratings
      const { data: workRatingsResults } = await queryUserRatings(
        {
          work: id,
          ratedBy: {
            $ne: user?.company ?? user?._id ?? null,
          },
        },
        null,
        -1
      );

      // * Get firstname and surname
      const { data: _users } = await queryUsers(
        {
          _id: {
            $in: workRatingsResults.map((s) => s.ratedBy),
          },
        },
        null,
        -1,
        {
          _id: 1,
          firstname: 1,
          surname: 1,
        }
      );

      const { data: _companies } = await queryCompanies(
        {
          _id: {
            $in: workRatingsResults.map((s) => s.ratedBy),
          },
        },
        null,
        -1,
        {
          _id: 1,
          name: 1,
        }
      );

      // * Set reviews
      setReviews(
        workRatingsResults.map((review) => {
          // Use find instead of map for users to get the matching user
          const _user = _users.find((u) => review.ratedBy === u._id);
          const _company = _companies.find(
            (company) => review.ratedBy === company._id
          );

          if (!_user && !_company) return review;

          return {
            ...review,
            ratedBy: _company
              ? {
                  name: _company.name,
                }
              : {
                  name: `${_user.firstname} ${_user.surname ?? ""}`,
                },
          };
        })
      );

      _work.rating = _rating.average;

      if (_work.type === 1) {
        setIsPanorama(true);

        // * Check if work has a panorama
        if (_work.panorama?.images) {
          setPanoramas(_work.panorama.images);
          setCurrentPanoramaIndex(0);
        }

        // * Check if have cycles
        if (_work.panorama?.cycles) {
          setCycles(_work.panorama?.cycles);
        }

        // * Check if have materials
        if (_work.panorama?.materials) {
          setMaterials(_work.panorama?.materials);
        }

        // * Check if have floor plans
        if (_work.floorPlans) {
          setFloorPlans(_work.floorPlans);
        }
      }

      if (_work.type === 2) {
        setIsPanorama(false);
      }

      // * ===== Get more works and recommendation ====
      const { data: _moreWorks } = await queryWorks(
        {
          _id: {
            $ne: _work._id,
          },
          type: { $in: [1, 3] },
          createdBy: _work.createdBy,
          active: true,
          deleted: false,
        },
        1,
        4,
        {
          banner: 1,
          name: 1,
          desc: 1,
          designType: 1,
          size: 1,
          createdBy: 1,
          program: 1,
          renderer: 1,
          views: 1,
          type: 1,
        }
      );

      const { data: _recommendations } = await queryWorks(
        {
          _id: {
            $ne: _work._id,
          },
          type: { $in: [1, 3] },
          active: true,
          deleted: false,
        },
        1,
        4,
        {
          banner: 1,
          name: 1,
          desc: 1,
          designType: 1,
          createdBy: 1,
          size: 1,
          program: 1,
          renderer: 1,
          views: 1,
          type: 1,
        }
      );

      // * Get users for each work
      const { data: _usersFound } = await queryUsers(
        {
          _id: {
            $in: [
              ..._recommendations.map((r) => r.createdBy),
              ..._moreWorks.map((r) => r.createdBy),
            ],
          },
        },
        null,
        -1,
        { firstname: 1, surname: 1, avatar: 1, plan: 1 }
      );

      setMoreWorksByAuthor(
        _moreWorks.map((work) => {
          const { firstname, surname, avatar, plan } = _usersFound.find(
            (u) => u._id === work.createdBy
          );

          return {
            ...work,
            creator: { avatar, name: `${firstname} ${surname || ""}`, plan },
          };
        })
      );

      setRecommendations(
        _recommendations.map((r) => {
          const { firstname, surname, avatar, plan } = _usersFound.find(
            (u) => u._id === r.createdBy
          );

          return {
            ...r,
            creator: { avatar, name: `${firstname} ${surname || ""}`, plan },
          };
        })
      );

      setWork(_work);
    } catch (error) {
      console.error(error);
      toast.error("Unable to get project");
    }
  };

  const _setIsMusicPlaying = (value) => {
    if (value) {
      widgetRef.current.play();
    }

    if (!value) {
      widgetRef.current.pause();
    }

    setIsMusicPlaying(value);
    isMusicPlayingRef.current = value;
  };

  const handleToggleMusic = () => {
    _setIsMusicPlaying(!isMusicPlayingRef.current);
  };

  const handleOwnerClick = () => {
    if (work.company) {
      viewCompany(work.company._id);
      return;
    }

    viewProfile(work.owner._id);
  };

  const handleLikeClick = async () => {
    if (!isLiked) {
      if (!(await addLikes(id))) return;
      setIsLiked(true);
      return;
    }

    await removeLikes(id);
    setIsLiked(false);
  };

  const handleShareClick = () => {
    setIsToggleShare(true);
  };

  const handleGalleryImageClick = (index) => {
    console.log("index", index);
    setSelectedImage(index);
  };

  const handleOnReviewed = () => {};

  const handleChatClick = () => {
    if (!isAuth) {
      toggleAuthModal(true, undefined, `/projects/${id}?chat`);
      return;
    }

    startChat(work.company ?? work.createdBy, user._id, {
      work: id,
    });
  };

  if (!work) {
    return <Loading onClose={onClose} />;
  }

  const {
    name,
    desc,
    price,
    size,
    designType,
    owner,
    banner,
    images,
    type,
    music,
    rating,
    program,
    renderer,
    location: _location,
    watermark,
  } = work;

  // TODO: Render OTHER section if already bought
  return (
    <Overlay
      onClose={onClose}
      style={{
        alignItems: "flex-start",
      }}
    >
      <div className="project-view">
        <div className="project-view-close" onClick={onClose}>
          <CloseSvg />
          {TranslationCommon("Close")}
        </div>
        <div className="project-view-content">
          <div className="project-view-content-ctas">
            <div
              className={`project-view-content-ctas-action project-view-content-ctas-action--avatar ${
                owner.isOwner
                  ? "project-view-content-ctas-action--avatar--is-owner"
                  : ""
              }`}
              onClick={handleOwnerClick}
              role="button"
            >
              <img
                alt="avatar"
                src={`${assetsUri}/${owner.avatar}`}
                onError={({ target }) => {
                  target.src = owner.avatar;
                }}
              />
            </div>
            <div
              className="project-view-content-ctas-action project-view-content-ctas-action--like"
              onClick={handleLikeClick}
            >
              {isLiked ? (
                <LikeFilledSvg
                  className="project-view-content-ctas-action--like-icon"
                  style={{ marginTop: "3px" }}
                  width={28}
                />
              ) : (
                <LikeSvg
                  className="project-view-content-ctas-action--like-icon"
                  style={{ marginTop: "3px" }}
                  width={28}
                />
              )}
            </div>
            <div
              className="project-view-content-ctas-action project-view-content-ctas-action--share"
              onClick={handleShareClick}
            >
              <ShareWhiteSvg width={26} style={{ marginLeft: "-3px" }} />
            </div>
            <div
              className="project-view-content-ctas-action project-view-content-ctas-action--close"
              onClick={onClose}
            >
              <CloseSvg />
              {TranslationCommon("Close")}
            </div>
          </div>
          <div className="project-view-content-container">
            <div
              className={`project-view-content-container-banner project-view-content-container-banner--${
                isPanorama ? "panorama" : "images"
              }`}
            >
              {isPanorama && (
                <div className="project-view-content-container-banner-selected">
                  <Panorama
                    workId={id}
                    panoramas={panoramas}
                    isHaveMusic={music}
                    isMusicPlaying={isMusicPlayingRef.current}
                    onToggleMusic={handleToggleMusic}
                    cycles={cycles}
                    materials={materials}
                    floorPlans={floorPlans}
                    work={work}
                    fullScreen={isFullscreen}
                    isLiked={isLiked}
                    onLikeChanged={(liked) => setIsLiked(liked)}
                    onShare={handleShareClick}
                    watermark={watermark}
                    rating={rating ?? 0}
                  />
                </div>
              )}
              {!isPanorama && (
                <>
                  <div className="project-view-content-container-banner-selected">
                    <img
                      className="project-view-content-container-banner-selected__image"
                      alt="banner"
                      src={`${assetsUri}/${images[selectedImage]?.path}`}
                    />
                  </div>
                  <div className="project-view-content-container-banner-gallery-container">
                    <img
                      role="button"
                      ref={prevRef}
                      src={prevImg}
                      className="project-view-content-container-banner-gallery__prev-button"
                      width={50}
                      height={50}
                    />
                    <img
                      role="button"
                      ref={nextRef}
                      src={nextImg}
                      className="project-view-content-container-banner-gallery__next-button"
                      width={50}
                      height={50}
                    />
                    <div className="project-view-content-container-banner-gallery">
                      <Swiper
                        modules={[Navigation, Pagination]}
                        direction="horizontal"
                        spaceBetween={20}
                        slidesPerView={3}
                        navigation={{
                          prevEl: prevRef.current,
                          nextEl: nextRef.current,
                        }}
                        observer={true}
                        onInit={() => setInit(true)}
                        breakpoints={{
                          769: {
                            direction: "vertical",
                          },
                        }}
                      >
                        {images.map((image, index) => {
                          return (
                            <SwiperSlide key={index}>
                              <img
                                className="project-view-content-container-banner-gallery-item"
                                style={{
                                  opacity: index === selectedImage ? 1 : 0.5,
                                }}
                                alt="banner"
                                src={`${assetsUri}/${image.path}`}
                                onClick={() => handleGalleryImageClick(index)}
                              />
                            </SwiperSlide>
                          );
                        })}
                      </Swiper>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="project-view-content-container-info">
              <div className="project-view-content-container-info-categories-container">
                <div className="project-view-content-container-info-categories">
                  {_location && (
                    <div className="project-view-content-container-info-categories__category">
                      <PinSvg
                        width={14}
                        style={{ marginTop: 2, marginLeft: -4, marginRight: 4 }}
                      />
                      {_location}
                    </div>
                  )}
                  {size && size > 0 ? (
                    <div className="project-view-content-container-info-categories__category">
                      {(+size).toLocaleString()} {TranslationCommon("sq.m")}
                    </div>
                  ) : (
                    <></>
                  )}
                  <div className="project-view-content-container-info-categories__category">
                    {TranslationWorkType(
                      designStyles.works.find(
                        (w) => w.value === designType.work
                      )?.name || designType.work
                    )}
                  </div>
                  <div className="project-view-content-container-info-categories__category">
                    {TranslationSpaceType(
                      designStyles.spaces.find(
                        (w) => w.value === designType.space
                      )?.name || designType.space
                    )}
                  </div>
                  {Array.isArray(designType.style) ? (
                    designType.style.map((style) => {
                      return (
                        <div
                          key={style}
                          className="project-view-content-container-info-categories__category"
                        >
                          {TranslationStyleType(
                            designStyles.styles.find((w) => w.value === style)
                              ?.name || style
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="project-view-content-container-info-categories__category">
                      {TranslationStyleType(
                        designStyles.styles.find(
                          (w) => w.value === designType.style
                        )?.name || designType.style
                      )}
                    </div>
                  )}
                </div>
              </div>
              {price ? (
                <div className="project-view-content-container-info-other">
                  <div className="project-view-content-container-info-other-price">
                    <span className="project-view-content-container-info-other-price__value">
                      {price.toLocaleString()}
                    </span>
                    <span className="project-view-content-container-info-other-price__currency">
                      THB
                    </span>
                  </div>
                  {size && size > 0 ? (
                    <span className="project-view-content-container-info-other-area-price">
                      {`(${(
                        +price / +size
                      ).toLocaleString()} THB ${TranslationCommon(
                        "per"
                      )} ${TranslationCommon("sq.m")})`}
                    </span>
                  ) : (
                    <span className="project-view-content-container-info-other-area-price">
                      Estimated Price
                    </span>
                  )}
                </div>
              ) : null}
              <div className="project-view-content-container-info-desc">
                <span className="project-view-content-container-info-desc__title">
                  {name}
                </span>
                <div className="project-view-content-container-info-desc__rating">
                  <RatingStar score={rating} readOnly />
                  {program && (
                    <img
                      className="project-desc-detail-programs__icon"
                      src={PROGRAMS[program]}
                      width={18}
                    />
                  )}
                  {renderer && (
                    <img
                      className="project-desc-detail-programs__icon"
                      src={RENDERERS[renderer]}
                      width={18}
                    />
                  )}
                </div>
                <span className="project-view-content-container-info-desc__desc">
                  {desc}
                </span>
                <div className="project-view-content-container-info-desc__avatar">
                  <img
                    src={`${assetsUri}/${owner.avatar}`}
                    onError={({ target }) => {
                      target.src = owner.avatar;
                    }}
                    onClick={handleOwnerClick}
                  />
                  <div className="project-view-content-container-info-desc__avatar__info">
                    <label onClick={handleOwnerClick}>{owner.name}</label>
                    {!isCreator && (
                      <div
                        className="project-view-content-container-info-desc__avatar__info__chat"
                        onClick={handleChatClick}
                      >
                        <MessageSvg
                          className="project-view-content-container-info-desc__avatar__info__chat__icon"
                          width="22"
                          height="22"
                        />
                        <span className="project-view-content-container-info-desc__avatar__info__chat__text">
                          Get in touch
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <MoreBy
              title={`${TranslationCommon("More by")} ${owner.name}`}
              works={moreWorksByAuthor}
              delay={3000}
              key={1}
            />
            <MoreBy
              title={TranslationCommon("Recommended")}
              works={recommendations}
              delay={5000}
              key={2}
            />
            {reviews && reviews.length > 0 && (
              <div className="project-view-reviews">
                <div className="project-view-reviews__heading">
                  What the community is saying
                </div>
                <Swiper
                  modules={[Navigation, Autoplay, Pagination]}
                  slidesPerView={1}
                  spaceBetween={20}
                  loop={false}
                  autoplay={{
                    delay: 8000,
                  }}
                  pagination={{ clickable: true }}
                  breakpoints={{
                    768: {
                      slidesPerView: 2,
                    },
                    1252: {
                      slidesPerView: 3,
                    },
                  }}
                  className="project-view-reviews-list"
                >
                  {reviews.map((r) => (
                    <SwiperSlide>
                      <div className="project-view-reviews-content">
                        <div className="project-view-reviews-content-wrp">
                          <RatingStar score={r.rate} size={24} readOnly />
                          <div className="project-view-reviews-content__name">
                            {r.ratedBy?.name}
                          </div>
                          <div className="project-view-reviews-content__update">
                            {fromNow(r.createdOn)}
                          </div>
                          <div className="project-view-reviews-content__review">
                            {r.msg}
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}
            {work.createdBy !== user?._id && (
              <Review
                workId={id}
                workCreator={work.createdBy}
                onReviewed={handleOnReviewed}
              />
            )}
          </div>
        </div>
      </div>
      {isToggleShare && (
        <Share
          link={`https://urbaan.app/projects/${id}`}
          thumbnail={work.banner}
          onClose={() => {
            setIsToggleShare(false);
          }}
        />
      )}
      {work.music && typeof work.music === "string" && (
        <div className="pannellum-soundcloud">
          <iframe
            ref={ref}
            id="soundcloud-widget"
            width="100%"
            height="64"
            scrolling="no"
            frameBorder="no"
            allow="autoplay"
            src={`https://w.soundcloud.com/player/?url=${work.music}&amp;show_artwork=false&amp;color=ff5500&amp;auto_play=true&amp;hide_related=true&amp;show_comments=false&amp;show_user=true&amp;show_reposts=false`}
          ></iframe>
        </div>
      )}
    </Overlay>
  );
};

ProjectView.propTypes = {
  id: PropTypes.string,
  onClose: PropTypes.func,
};

ProjectView.defaultProps = {
  onClose: () => {},
};

export default ProjectView;
